import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DollarSign, RotateCcw, XCircle, Package as PackageIcon, Check, Star, Eye, Bell } from "lucide-react";
import RecordDrawer from "./RecordDrawer";
import PaidViaDialog from "./PaidViaDialog";
import ShipDialog from "./ShipDialog";
import { adminClient } from "./api";

const CONFIGS = {
  bookings: {
    endpoint: "/bookings",
    title: "Bookings",
    subtitle: "Every scheduled service job — Zelle deposits show as 'awaiting_payment' until you Mark Paid. Customers with an email get notified on every status change.",
    cols: ["ref_id", "service_type", "name", "phone", "sms_consent", "address", "preferred_date", "preferred_window", "payment_method", "status", "created_at"],
    headers: ["Ref", "Service", "Name", "Phone", "Texts", "Address", "Date", "Window", "Pay", "Status", "Created"],
    statusEndpoint: (id) => `/admin/bookings/${id}/status`,
    actions: [
      { key: "scheduled", label: "Mark Paid", icon: Check, className: "bg-emerald-600 hover:bg-emerald-700 text-white", hideWhen: (r) => r.status === "scheduled" || r.status === "completed" },
      { key: "completed", label: "Completed", icon: Check, variant: "outline", hideWhen: (r) => r.status !== "scheduled" && r.status !== "in_progress" },
      { key: "deposit_reminder", label: "Remind deposit", icon: Bell, variant: "outline", className: "text-amber-900 border-amber-400 bg-amber-50 hover:bg-amber-100",
        custom: "deposit_reminder", hideWhen: (r) => r.status !== "awaiting_payment" || !r.email },
      { key: "day_before", label: "Send day-before", icon: Bell, variant: "outline", className: "text-sky-900 border-sky-300 bg-sky-50 hover:bg-sky-100",
        custom: "day_before", hideWhen: (r) => !["scheduled", "awaiting_payment"].includes(r.status) || (!r.email && !r.sms_consent) },
      { key: "review_request", label: "Ask for review", icon: Star, variant: "outline", className: "text-[#0C2E2B] border-[#FFDF00] bg-[#FFDF00]/20 hover:bg-[#FFDF00]/40",
        custom: true, hideWhen: (r) => r.status !== "completed" || !r.email || !!r.review_request_sent_at || !!r.reviewed_at },
      { key: "refunded", label: "Refunded", icon: RotateCcw, variant: "outline", hideWhen: (r) => r.status === "refunded" },
      { key: "cancelled", label: "Cancel", icon: XCircle, variant: "outline", className: "text-red-600 hover:text-red-700", hideWhen: (r) => r.status === "cancelled" },
    ],
  },
  inquiries: {
    endpoint: "/admin/inquiries",
    title: "Inquiries",
    subtitle: "Marketplace item questions.",
    cols: ["item_title", "name", "phone", "sms_consent", "email", "message", "created_at"],
    headers: ["Item", "Name", "Phone", "Texts", "Email", "Message", "Created"],
    actions: [],
  },
  orders: {
    endpoint: "/admin/orders",
    title: "Merch Orders",
    subtitle: "Cart checkouts. Zelle orders wait for you to Mark Paid; card orders auto-mark via Stripe. Customers are emailed on Paid / Shipped / Refunded / Cancelled.",
    cols: ["ref_id", "name", "email", "phone", "sms_consent", "address", "total", "payment_method", "status", "created_at"],
    headers: ["Ref", "Name", "Email", "Phone", "Texts", "Address", "Total", "Pay", "Status", "Created"],
    statusEndpoint: (id) => `/admin/orders/${id}/status`,
    actions: [
      { key: "paid", label: "Mark Paid", icon: Check, className: "bg-emerald-600 hover:bg-emerald-700 text-white", hideWhen: (r) => r.status === "paid" || r.status === "shipped" },
      { key: "shipped", label: "Shipped", icon: PackageIcon, variant: "outline", hideWhen: (r) => r.status !== "paid" },
      { key: "refunded", label: "Refunded", icon: RotateCcw, variant: "outline", hideWhen: (r) => r.status === "refunded" },
      { key: "cancelled", label: "Cancel", icon: XCircle, variant: "outline", className: "text-red-600 hover:text-red-700", hideWhen: (r) => r.status === "cancelled" },
    ],
  },
};

const STATUS_COLORS = {
  awaiting_payment: "bg-amber-100 text-amber-900",
  scheduled: "bg-emerald-100 text-emerald-900",
  paid: "bg-emerald-100 text-emerald-900",
  completed: "bg-emerald-100 text-emerald-900",
  in_progress: "bg-blue-100 text-blue-900",
  shipped: "bg-blue-100 text-blue-900",
  refunded: "bg-purple-100 text-purple-900",
  cancelled: "bg-red-100 text-red-900",
};

export default function OpsList({ kind }) {
  const cfg = CONFIGS[kind];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const drawerKind = kind === "bookings" ? "booking" : kind === "inquiries" ? "inquiry" : kind === "orders" ? "order" : null;

  const load = useCallback(() => {
    setLoading(true);
    return adminClient.get(cfg.endpoint)
      .then(({ data }) => setItems(Array.isArray(data) ? data : (data.items || [])))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [cfg.endpoint]);

  useEffect(() => { load(); }, [load]);

  const [paidFor, setPaidFor] = useState(null);

  const [shipFor, setShipFor] = useState(null);

  const changeStatus = async (row, status, paidVia, tracking) => {
    if (!cfg.statusEndpoint) return;
    const isPaid = status === "paid" || status === "scheduled";
    if (isPaid && !paidVia) { setPaidFor({ row, status }); return; }
    if (status === "shipped" && kind === "orders" && !tracking) { setShipFor(row); return; }
    setUpdatingId(row.id);
    try {
      await adminClient.put(cfg.statusEndpoint(row.id), { status, site_url: window.location.origin, paid_via: paidVia || undefined, ...(tracking || {}) });
      toast.success(isPaid ? `Marked paid via ${paidVia}` : `Marked ${status}`);
      await load();
    } catch {
      toast.error("Update failed");
    } finally {
      setUpdatingId(null);
    }
  };

  const sendReviewRequest = async (row) => {
    setUpdatingId(row.id);
    try {
      await adminClient.post(`/admin/bookings/${row.id}/review-request`, { site_url: window.location.origin });
      toast.success(`Review request emailed to ${row.email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send review request");
    } finally {
      setUpdatingId(null);
    }
  };

  const sendDepositReminder = async (row) => {
    setUpdatingId(row.id);
    try {
      await adminClient.post(`/admin/bookings/${row.id}/deposit-reminder`);
      toast.success(`Deposit reminder emailed to ${row.email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminder");
    } finally {
      setUpdatingId(null);
    }
  };

  const sendDayBefore = async (row) => {
    setUpdatingId(row.id);
    try {
      const { data } = await adminClient.post(`/admin/bookings/${row.id}/day-before`);
      toast.success(`Day-before reminder sent via ${data.sent_via.join(" + ")}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminder", { duration: 8000 });
    } finally {
      setUpdatingId(null);
    }
  };

  const renderCell = (row, c) => {
    if (c === "preferred_date" && row.day_before_sent_at) {
      return (
        <div>
          <div>{row.preferred_date}</div>
          <div data-testid={`daybefore-note-${row.id}`} className="text-[10px] text-sky-700 whitespace-nowrap">Day-before sent</div>
        </div>
      );
    }
    if (c === "status" && row.status === "awaiting_payment" && row.deposit_reminder_sent_at) {
      return (
        <div className="space-y-1">
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_COLORS[row.status] || "bg-zinc-100 text-zinc-800"}`}>awaiting payment</span>
          <div data-testid={`reminder-note-${row.id}`} className="text-[10px] text-zinc-500 whitespace-nowrap">Reminded {new Date(row.deposit_reminder_sent_at).toLocaleDateString()}</div>
        </div>
      );
    }
    if (c === "status" && row.status === "completed" && (row.reviewed_at || row.review_request_sent_at || row.review_request_due_at)) {
      const note = row.reviewed_at ? `Reviewed ${row.review_rating || ""}★` : row.review_request_sent_at ? "Review asked" : `Review ask ${new Date(row.review_request_due_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric" })}`;
      return (
        <div className="space-y-1">
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_COLORS[row.status] || "bg-zinc-100 text-zinc-800"}`}>completed</span>
          <div data-testid={`review-note-${row.id}`} className="text-[10px] text-zinc-500 whitespace-nowrap">{note}</div>
        </div>
      );
    }
    if (c === "created_at" && row[c]) return new Date(row[c]).toLocaleString();
    if (c === "total" && row[c] != null) return `$${Number(row[c]).toFixed(2)}`;
    if (c === "status") {
      const s = row[c] || "—";
      return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_COLORS[s] || "bg-zinc-100 text-zinc-700"}`}>{s.replace(/_/g, " ")}</span>;
    }
    if (c === "sms_consent") {
      if (row.sms_opted_out) return <span data-testid={`sms-flag-${row.id}`} className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-100 text-red-800">Do not text</span>;
      if (row.sms_consent) return <span data-testid={`sms-flag-${row.id}`} className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-900">Opted in</span>;
      return <span data-testid={`sms-flag-${row.id}`} className="text-zinc-400">—</span>;
    }
    if (c === "payment_method") {
      const m = row[c];
      if (!m) return "—";
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold">
          {m === "zelle" ? <DollarSign className="w-3 h-3 text-emerald-600" /> : <PackageIcon className="w-3 h-3 text-blue-600" />}
          {m}
        </span>
      );
    }
    return row[c] ?? "—";
  };

  return (
    <div data-testid={`${kind}-admin-page`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-2xl text-[#0C2E2B]">{cfg.title}</h1>
          <p className="text-sm text-zinc-500">{cfg.subtitle}</p>
        </div>
        <Badge className="bg-[#0C2E2B] text-[#FFDF00] hover:bg-[#0C2E2B]">{items.length} total</Badge>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading...</div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-zinc-500 text-sm">No records yet.</Card>
      ) : (
        <Card className="overflow-auto border-2 border-zinc-200">
          <table className="w-full text-xs">
            <thead className="bg-[#0C2E2B] text-white">
              <tr>
                {cfg.headers.map((h) => (
                  <th key={h} className="text-left px-3 py-2 font-tactical uppercase tracking-widest text-[10px]">{h}</th>
                ))}
                {(cfg.actions?.length > 0 || drawerKind) && <th className="px-3 py-2"> </th>}
              </tr>
            </thead>
            <tbody>
              {items.map((row, idx) => (
                <tr key={row.id || row.ref_id || idx} data-testid={`${kind}-row-${row.ref_id || idx}`} className="border-t border-zinc-200 hover:bg-zinc-50">
                  {cfg.cols.map((c) => (
                    <td key={c} className="px-3 py-2 text-zinc-700 align-top">{renderCell(row, c)}</td>
                  ))}
                  {(cfg.actions?.length > 0 || drawerKind) && (
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex gap-1 justify-end">
                        {drawerKind && (
                          <Button
                            data-testid={`${kind}-action-details-${row.ref_id || row.id}`}
                            size="sm"
                            variant="outline"
                            className="h-7 text-[10px] font-bold rounded-md"
                            onClick={() => setOpenId(row.id)}
                          >
                            <Eye className="w-3 h-3 mr-1" /> Details
                          </Button>
                        )}
                        {cfg.actions.map((a) => {
                          if (a.hideWhen && a.hideWhen(row)) return null;
                          return (
                            <Button
                              key={a.key}
                              data-testid={`${kind}-action-${a.key}-${row.ref_id}`}
                              size="sm"
                              variant={a.variant || "default"}
                              className={`h-7 text-[10px] font-bold rounded-md ${a.className || "btn-kinetic"}`}
                              disabled={updatingId === row.id}
                              onClick={() => (a.custom === "deposit_reminder" ? sendDepositReminder(row) : a.custom === "day_before" ? sendDayBefore(row) : a.custom ? sendReviewRequest(row) : changeStatus(row, a.key))}
                            >
                              <a.icon className="w-3 h-3 mr-1" /> {a.label}
                            </Button>
                          );
                        })}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <PaidViaDialog
        open={!!paidFor}
        onClose={() => setPaidFor(null)}
        amountLabel={paidFor ? (kind === "orders" ? `$${Number(paidFor.row.total || 0).toFixed(2)}` : `the $${Number(paidFor.row.deposit_amount || 25).toFixed(2)} deposit`) : ""}
        onConfirm={async (method) => { const { row, status } = paidFor; setPaidFor(null); await changeStatus(row, status, method); }}
      />
      <ShipDialog
        open={!!shipFor}
        order={shipFor}
        onClose={() => setShipFor(null)}
        onConfirm={async (tracking) => { const row = shipFor; setShipFor(null); await changeStatus(row, "shipped", null, tracking); }}
      />
      {drawerKind && <RecordDrawer kind={drawerKind} recordId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
